import style from './Login.module.scss';
import Logo from '@/components/Logo';
import { Loader } from '@ax/ui';
import { useNavigate } from 'react-router-dom';
import { useActivity, useLogin, useProviderLogin } from '@/hooks/api';
import { useForm } from 'react-hook-form';
import { ProviderLoginInput } from '@ax/service/src/auth-interface';
import { Auth } from '@ax/auth';
import { useEffect, useState } from 'react';
import { routerPaths, ANALYTICS_CONSTANTS, LoginStage } from '@ax/constants';
import useGetBrowserInfo from '@/hooks/useGetBrowserInfo';
import { PasswordForm } from './PasswordForm';
import { getAuthToken, startSession } from '@ax/service/src/sso-service.tsx';
import { getUserSSODetails } from '@ax/service/src/user-management-service.tsx';
import { GetUserSSODetailsResponse } from '@ax/service/src/user-management-interface.ts';
import useAnalytics from '@/hooks/useAnalytics.tsx';
import { PasscodeForm } from '@/modules/Auth/Login/PasscodeForm.tsx';
import { setProviderPasscode } from '@ax/service';
import { usePasscodeSession } from '@/hooks/usePasscodeSession.tsx';
import { PasscodeChallengeForm } from '@/modules/Auth/Login/PasscodeChallengeForm.tsx';
import { useProviderPasscodeLogin } from '@/hooks/api/useProviderPasscodeLogin.tsx';
import {
  useAppDispatch,
  userSignedIn,
  useAppSelector,
  loginStageChanged,
  selectProviderName,
  selectLoginStage,
  passcodeLoginAttemptsExceeded,
} from '@ax/redux-store';
import { AxiosError } from 'axios';
import { UsernameDomainForm } from '@/modules/Auth/Login/UsernameDomainForm.tsx';
import { useLoginDomains } from '@/hooks/useLoginDomains';

const Login = () => {
  const STORAGE_KEY_USERNAME = 'sso_redirected_username';
  const [isUsernameValidating, setIsUsernameValidating] = useState(false);
  const stage = useAppSelector(selectLoginStage);
  const [username, setUsername] = useState('');
  const [ssoUrl, setSsoUrl] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [loginError, setLoginError] = useState(false);
  const [newPasscode, setNewPasscode] = useState('');
  const navigate = useNavigate();
  const browserInfo = useGetBrowserInfo();
  const analytics = useAnalytics();
  const {
    session,
    isSessionInitialized,
    failedPasscodeLoginAttempts,
    isSessionValid,
    createSession,
    clearSession,
    clearFailedPasscodeLoginAttempts,
    incrementFailedPasscodeLoginAttempts,
    forcePasscodeReset,
    clearForcePasscodeReset,
  } = usePasscodeSession();
  const [isPasscodeFormLoading, setIsPasscodeFormLoading] = useState(false);

  const {
    register,
    handleSubmit,
    setError,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm({
    mode: 'onSubmit',
  });

  const { mutateAsync: auth, isLoading, isError, error } = useProviderLogin();
  const {
    mutateAsync: passcodeAuth,
    isLoading: isPasscodeAuthLoading,
    isError: isPasscodeAuthError,
  } = useProviderPasscodeLogin();
  const { mutateAsync: login, isLoading: isLoginLoading, error: loginErrorMessage } = useLogin();
  const { mutateAsync: activity, isLoading: isActivityLoading } = useActivity();
  const dispatch = useAppDispatch();
  const providerName = useAppSelector(selectProviderName);
  const { clearValue } = useLoginDomains();

  const handleSuccessfulLogin = async (
    token: string,
    method: string = ANALYTICS_CONSTANTS.loginMethods.password,
  ) => {
    Auth.saveUser(token ?? '');
    if (Auth.isLoggedIn()) {
      try {
        await login({
          browser: `${browserInfo.name} ${browserInfo.version}`,
        });
      } catch {
        console.error('Error while calling the GWP login functionality.');
      }

      await activity();
      analytics.onUserLogin({ providerEmail: username, method });
      dispatch(
        userSignedIn({
          username,
          timestamp: Date.now(),
        }),
      );
    }
  };

  const handleUsernameFormSubmit = async (data: ProviderLoginInput) => {
    analytics.onUsernameInput({ providerEmail: data.email });
    setIsUsernameValidating(true);
    if (data.email) {
      let ssoDetails: GetUserSSODetailsResponse;

      try {
        ssoDetails = await getUserSSODetails({ userName: data.email });
        setUsername(data.email);

        if (ssoDetails.ssoEnabled) {
          try {
            const response = await startSession({ userName: data.email, appName: 'gwp' });
            if (response.url) {
              setIsUsernameValidating(false);
              setSsoUrl(response.url);
              dispatch(loginStageChanged({ stage: LoginStage.SSOChallenge, previousStage: stage }));
              localStorage.setItem(STORAGE_KEY_USERNAME, data.email);
              analytics.onSSORedirect({ providerEmail: data.email, redirectUrl: response.url });
            } else {
              console.warn('SSO session start failed. The SSO URL is not configured.');
              setIsUsernameValidating(false);
              setError('email', {
                message: 'There was an error initiating the SSO session. Please, contact support.',
              });
              return;
            }
          } catch (err) {
            console.error(err);
            setIsUsernameValidating(false);
            setError('email', {
              message: 'There was an error initiating the SSO session. Please, try again.',
            });
            return;
          }
        } else {
          setIsUsernameValidating(false);
          dispatch(
            loginStageChanged({ stage: LoginStage.PasswordChallenge, previousStage: stage }),
          );
        }
      } catch (err: any) {
        console.error(err);
        setIsUsernameValidating(false);
        const { response } = err;

        if (response?.status === 400) {
          const message = response?.data?.message.match(/blocked/i)
            ? 'Provider account is not active.'
            : response?.data?.message.match(/archived/i)
            ? 'Account is currently archived'
            : response?.data?.message;

          setError('email', { message });
          setError('password', { message });
          return;
        }

        setError('email', { message: 'Email address not found. Try again or sign up.' });
        clearValue('@' + data.email.split('@')[1]);
        return;
      }
    }
  };

  const handlePasscodeSetup = async (passcode: string) => {
    setIsPasscodeFormLoading(true);

    try {
      await setProviderPasscode(passcode);
      createSession(providerName ?? username, username);
      clearForcePasscodeReset();
      clearFailedPasscodeLoginAttempts();
      dispatch(
        loginStageChanged({ stage: LoginStage.CreatePasscodeSuccess, previousStage: stage }),
      );
      analytics.onCreatePin({ providerEmail: username });
      navigate(routerPaths.notes);
    } catch (err) {
      console.error(err);
      return;
    } finally {
      setIsPasscodeFormLoading(false);
    }
  };

  const handlePasswordFormSubmit = async (data: ProviderLoginInput) => {
    try {
      const response = await auth(data);
      if (response && response?.token) {
        dispatch(loginStageChanged({ stage: LoginStage.PasswordSuccess, previousStage: stage }));
        await handleSuccessfulLogin(response.token, ANALYTICS_CONSTANTS.loginMethods.password);
      }
    } catch (err) {
      analytics.onUserLoginFailedPWD({ providerEmail: data.email });
      setLoginError(true);
    }
  };

  const handlePasscodeFormSubmit = async (username: string, passcode: string) => {
    try {
      const response = await passcodeAuth({ username, passcode });
      if (response && response?.token) {
        dispatch(loginStageChanged({ stage: LoginStage.PasscodeSuccess, previousStage: stage }));
        analytics.onEnteredValidPin({ providerEmail: username });
        await handleSuccessfulLogin(response.token, ANALYTICS_CONSTANTS.loginMethods.passcode);
        clearFailedPasscodeLoginAttempts();
      }
    } catch (err) {
      analytics.onEnteredInvalidPin({ providerEmail: username });
      setLoginError(true);
      incrementFailedPasscodeLoginAttempts();
    }
  };

  const renderButtonContent = () => {
    return isLoading || isLoginLoading || isActivityLoading || isUsernameValidating ? (
      <Loader color="#fff" />
    ) : (
      (() => {
        switch (stage) {
          case LoginStage.Identity:
            return 'Continue';
          case LoginStage.PasswordChallenge:
            return 'Log in';
          default:
            return 'Continue';
        }
      })()
    );
  };

  useEffect(() => {
    if (loginError) {
      Auth.logout();
      setErrorMessage(loginErrorMessage?.response?.data?.message);
      setLoginError(false);
    }

    if (Auth.isLoggedIn()) {
      navigate(routerPaths.notes);
    }

    const err = error as AxiosError<{ message?: string }>;

    if (isError) {
      if (
        err.response?.data?.message === 'Invalid email' ||
        err.response?.data?.message === 'Invalid password'
      ) {
        setError('password', { message: 'Re-enter password' });
      } else {
        const message = err.response?.data?.message?.match(/(active|blocked)/i)
          ? 'Provider account is not active.'
          : err.response?.data?.message?.match(/archived/i)
          ? 'Account is currently archived'
          : `An unknown error occurred. Please, try again or contact support if this continues to happen.`;

        setError('password', { message });
        setError('email', { message });
      }
    }

    const queryParams = new URLSearchParams(location.search);
    const isExpired = queryParams.get('expired') === 'yes';

    if (isExpired) {
      setErrorMessage('Session has expired. Please login again.');
    }
  }, [navigate, isError, error, location.search, loginError]);

  useEffect(() => {
    if (ssoUrl) {
      setTimeout(() => {
        location.href = ssoUrl;
      }, 1000);
    }
  }, [ssoUrl]);

  useEffect(() => {
    if (location.pathname === routerPaths.ssoSuccess) {
      dispatch(loginStageChanged({ stage: LoginStage.SSOSuccess, previousStage: stage }));
    }
  }, [location.pathname]);

  useEffect(() => {
    if (stage === LoginStage.Identity && location.pathname !== routerPaths.ssoSuccess) {
      localStorage.removeItem(STORAGE_KEY_USERNAME);
    }
  }, [stage]);

  useEffect(() => {
    if (stage === LoginStage.SSOSuccess) {
      const providerEmail = localStorage.getItem(STORAGE_KEY_USERNAME) || '';
      analytics.onSSOReturn({ providerEmail, method: ANALYTICS_CONSTANTS.loginMethods.sso });
      localStorage.removeItem(STORAGE_KEY_USERNAME);
    }
  }, [stage]);

  useEffect(() => {
    (async () => {
      {
        if (stage === LoginStage.SSOSuccess) {
          try {
            const tokenResponse = await getAuthToken();
            if (tokenResponse && tokenResponse?.token) {
              await handleSuccessfulLogin(
                tokenResponse.token,
                ANALYTICS_CONSTANTS.loginMethods.sso,
              );
            }
          } catch (err) {
            console.error(err);
            setErrorMessage('There was an error completing the SSO login. Please, try again.');
            setTimeout(() => {
              dispatch(loginStageChanged({ stage: LoginStage.Identity, previousStage: stage }));
              navigate(routerPaths.login);
            }, 1000);
          }
        }
      }
    })();
  }, [stage]);

  useEffect(() => {
    if (stage === LoginStage.Initialization && isSessionInitialized) {
      if (session && isSessionValid()) {
        dispatch(loginStageChanged({ stage: LoginStage.PasscodeChallenge, previousStage: stage }));
      } else if (session && !isSessionValid()) {
        clearSession();
        dispatch(loginStageChanged({ stage: LoginStage.Identity, previousStage: stage }));
      } else {
        dispatch(loginStageChanged({ stage: LoginStage.Identity, previousStage: stage }));
      }
    }
  }, [stage, session, isSessionValid, clearSession]);

  useEffect(() => {
    if (failedPasscodeLoginAttempts >= 3) {
      clearSession();
      forcePasscodeReset();
      setErrorMessage('Forced PIN reset due to too many failed attempts.');
      dispatch(passcodeLoginAttemptsExceeded());
    }
  }, [failedPasscodeLoginAttempts]);

  return (
    <div className={style.login__form__container}>
      <div className={style.login__form__header}>
        <div className={style.login__form__header__app_logo}>
          <Logo type="go" />
        </div>
      </div>
      {stage === LoginStage.Initialization ? (
        <div className={style.login__form__body}>
          <Loader color="#344bc6" />
        </div>
      ) : null}
      {stage === LoginStage.Identity ? (
        <UsernameDomainForm
          onSubmit={handleSubmit(handleUsernameFormSubmit)}
          message={errorMessage}
          errors={errors}
          setValue={setValue}
          clearErrors={clearErrors}
          renderButtonContent={renderButtonContent()}
        />
      ) : null}
      {stage === LoginStage.PasswordChallenge ? (
        <PasswordForm
          onSubmit={handleSubmit(handlePasswordFormSubmit)}
          message={errorMessage}
          errors={errors}
          username={username}
          register={register}
          renderButtonContent={renderButtonContent()}
        />
      ) : null}
      {stage === LoginStage.SSOChallenge ? (
        <div className={style.login__form__body}>
          <div className={style.login__form__body__message}>Redirecting to SSO login...</div>
        </div>
      ) : null}
      {[
        LoginStage.SSOSuccess,
        LoginStage.PasscodeSuccess,
        LoginStage.PasswordSuccess,
        LoginStage.CreatePasscodeSuccess,
      ].includes(stage) ? (
        <div className={style.login__form__body}>
          <div className={style.login__form__body__message}>Redirecting to Augmedix Go...</div>
        </div>
      ) : null}
      {stage === LoginStage.CreatePasscode ? (
        <PasscodeForm
          headerText={'Create a PIN'}
          username={username}
          onPasscodeInputCompleted={(value) => {
            setNewPasscode(value);
            dispatch(loginStageChanged({ stage: LoginStage.VerifyPasscode, previousStage: stage }));
          }}
        />
      ) : null}
      {stage === LoginStage.VerifyPasscode && newPasscode ? (
        <PasscodeForm
          headerText={isPasscodeFormLoading ? 'Setting up your PIN' : 'Verify your PIN'}
          passcode={newPasscode}
          isVerify={true}
          isLoading={isPasscodeFormLoading}
          onPasscodeInputCompleted={handlePasscodeSetup}
        />
      ) : null}
      {stage === LoginStage.PasscodeChallenge ? (
        <PasscodeChallengeForm
          onPasscodeInput={handlePasscodeFormSubmit}
          isLoading={isPasscodeAuthLoading}
          hasError={isPasscodeAuthError || failedPasscodeLoginAttempts > 0}
          errorMessage={
            failedPasscodeLoginAttempts > 0
              ? `${3 - failedPasscodeLoginAttempts} attempts left, then forced reset.`
              : ''
          }
          onChangeUsername={() => {
            analytics.onTapNotYou({ providerEmail: session?.email ?? '' });
            clearSession();
            dispatch(loginStageChanged({ stage: LoginStage.Identity, previousStage: stage }));
          }}
        />
      ) : null}
    </div>
  );
};

export default Login;
